import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import type { Me } from '@figureit/shared';
import { api } from '../api/client';

interface AuthContextValue {
  me: Me | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  currentOrgId: string | null;
  switchOrg: (orgId: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api<Me>('/api/auth/me');
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
  });

  const switchOrg = useCallback(
    async (orgId: string) => {
      await api('/api/auth/switch-org', {
        method: 'POST',
        body: JSON.stringify({ orgId }),
      });
      await qc.invalidateQueries();
    },
    [qc],
  );

  const logout = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' });
    qc.clear();
    window.location.href = '/login';
  }, [qc]);

  const refresh = useCallback(async () => {
    await meQuery.refetch();
  }, [meQuery]);

  const value = useMemo<AuthContextValue>(
    () => ({
      me: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isAuthenticated: !!meQuery.data,
      currentOrgId: meQuery.data?.currentOrgId ?? null,
      switchOrg,
      logout,
      refresh,
    }),
    [meQuery.data, meQuery.isLoading, switchOrg, logout, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = (): AuthContextValue => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth used outside AuthProvider');
  return v;
};
